import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  UserCircle,
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  Shield,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

const ROLES = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'company_admin', label: 'Company Admin' },
  { value: 'outlet_manager', label: 'Outlet Manager' },
  { value: 'cashier', label: 'Cashier' },
  { value: 'server', label: 'Server/Waiter' },
  { value: 'kitchen_staff', label: 'Kitchen Staff' },
  { value: 'expediter', label: 'Expediter' },
  { value: 'inventory_manager', label: 'Inventory Manager' },
];

export const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'cashier',
    outlet_id: '',
  });

  useEffect(() => {
    fetchUsers();
    fetchOutlets();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (e) {
      console.error('Error fetching users:', e);
    }
  };

  const fetchOutlets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/outlets`, { withCredentials: true });
      setOutlets(response.data);
    } catch (e) {
      console.error('Error fetching outlets:', e);
    }
  };

  const openEditModal = (u) => {
    setEditingUserId(u._id);
    setUserForm({
      email: u.email || '',
      password: '',
      name: u.name || '',
      role: u.role || 'cashier',
      outlet_id: u.outlet_id || '',
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    setLoading(true);
    try {
      if (editingUserId) {
        // Update — never send empty password
        const payload = {
          name: userForm.name,
          role: userForm.role,
          outlet_id: userForm.outlet_id || null,
        };
        await axios.put(`${API_URL}/api/users/${editingUserId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/auth/register`, userForm, { withCredentials: true });
      }
      await fetchUsers();
      setShowUserModal(false);
      resetForm();
    } catch (e) {
      console.error('Error saving user:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}`, { is_active: !isActive }, { withCredentials: true });
      await fetchUsers();
    } catch (e) {
      console.error('Error updating user:', e);
    }
  };

  const resetForm = () => {
    setEditingUserId(null);
    setUserForm({
      email: '',
      password: '',
      name: '',
      role: 'cashier',
      outlet_id: '',
    });
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getOutletName = (outletId) => {
    return outlets.find(o => o.id === outletId)?.name || '-';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-accent-red/10 text-red-700',
      company_admin: 'bg-primary/10 text-primary',
      outlet_manager: 'bg-accent-green/10 text-green-700',
      cashier: 'bg-accent-yellow/10 text-yellow-700',
      server: 'bg-blue-100 text-blue-700',
      kitchen_staff: 'bg-orange-100 text-orange-700',
      expediter: 'bg-purple-100 text-purple-700',
      inventory_manager: 'bg-teal-100 text-teal-700',
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Users & Roles</h1>
          <p className="text-muted-foreground mt-1">Manage staff accounts and permissions</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowUserModal(true); }}
          className="bg-primary hover:bg-primary-hover text-white"
          data-testid="add-user-btn"
        >
          <Plus size={18} className="mr-2" />
          Add User
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <Input
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="user-search"
        />
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">User</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Role</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Outlet</th>
              <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Active</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredUsers.map((user) => (
              <tr key={user._id} className="hover:bg-surface transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <UserCircle size={24} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                    {user.role?.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{getOutletName(user.outlet_id)}</td>
                <td className="px-4 py-3 text-center">
                  <Switch
                    checked={user.is_active !== false}
                    onCheckedChange={() => handleToggleActive(user._id, user.is_active !== false)}
                    disabled={user._id === currentUser?.id}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(user)}
                    data-testid={`edit-user-${user._id}`}
                    title="Edit user"
                  >
                    <PencilSimple size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      <Dialog
        open={showUserModal}
        onOpenChange={(o) => {
          setShowUserModal(o);
          if (!o) resetForm();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingUserId ? 'Edit User' : 'Add New User'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="John Doe"
                data-testid="user-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Email {editingUserId ? '' : '*'}</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="user-email"
                disabled={!!editingUserId}
              />
              {editingUserId && (
                <p className="text-xs text-muted-foreground">Email cannot be changed after creation</p>
              )}
            </div>

            {!editingUserId && (
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="user-password"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Role *</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
              >
                <SelectTrigger data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Outlet</Label>
              <Select
                value={userForm.outlet_id}
                onValueChange={(value) => setUserForm({ ...userForm, outlet_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select outlet" />
                </SelectTrigger>
                <SelectContent>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>{outlet.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setShowUserModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveUser}
              className="flex-1 bg-primary hover:bg-primary-hover text-white"
              disabled={
                loading ||
                !userForm.name ||
                (!editingUserId && (!userForm.email || !userForm.password))
              }
              data-testid="save-user-btn"
            >
              {loading
                ? (editingUserId ? 'Saving...' : 'Creating...')
                : (editingUserId ? 'Save Changes' : 'Create User')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
